/* eslint-disable camelcase */
import moment from 'moment';
import { get, omit, isNil } from 'lodash-es';
import { JOB_SITE_ACTIONS } from '~/app_constants';
import { mapActionToNewEnum } from '~/utils/job_sites';
import { datetimeWithZone, formatInternalDate } from '~/utils/time_utils';

export const getWoParams = (state) => ({
  woId: get(state, 'work_order.id'),
  organizationId: get(state, 'organization.id'),
});

export const getJobParams = (state, jobId) => ({
  ...getWoParams(state),
  jobId,
});

export const getJobScheduleParams = (state, scheduleId) => ({
  ...getWoParams(state),
  scheduleId,
});

export const prepareJobSitePayload = (payload, jobSite) => {
  const {
    enter_action,
    exit_action,
    address,
    position: { lat: latitude, lng: longitude },
    radius,
  } = jobSite;

  payload.job_site_rules = {
    exit_action: mapActionToNewEnum(exit_action),
    enter_action: mapActionToNewEnum(enter_action),
  };
  payload.job_site_attributes = { address, radius, latitude, longitude };
};

export const prepareBudgetPayload = (payload, hasResetBudget, budget) => {
  if (hasResetBudget) {
    payload.budget_attributes = { _destroy: '1' };

    return;
  }

  const { type, rate, start, hours_display: hd } = budget || {};

  if (isNil(type)) {
    payload.budget_attributes = null;

    return;
  }

  payload.budget_attributes = {
    ...omit(budget, ['alert', 'stop_timer', 'start', 'spent']),
    notification: budget.alert?.near_limit,
    stop_at: budget.stop_timer?.limit,
    rate: rate || '0',
    start_date: start ? formatInternalDate(start) : null,
    ...(hd ? { hours: hd / 60 / 60 } : {}),
  };
};

/**
 * Some nice helpers to help clean the data
 */
export function sortAssignees(ids, assignees) {
  if (!ids || !ids.length || !assignees || !assignees.length) {
    return [];
  }

  return ids
    .map((id) => assignees.find((user) => user.id === id))
    .filter((user) => !!user)
    .sort((userOne, userTwo) => userOne.name.localeCompare(userTwo.name));
}

export function cleanWorkOrder(workOrder = {}, state = {}, getters, tz) {
  let upcomingJob = null;
  const { clients = [], wo_assignees = [] } = state;
  const { job_site, assignee_ids, client_id, wo_states, upcoming_job: originalUpcomingJob } = workOrder;
  const client = clients.find((client) => client.id === client_id);

  if (originalUpcomingJob) {
    const { stopsAt, startsAt } = cleanJobDateTime(originalUpcomingJob, tz);

    upcomingJob = {
      ...originalUpcomingJob,
      stopsAt,
      startsAt,
      timeZoneAbbr: tz && startsAt ? startsAt.zoneAbbr() : null,
    };
  }

  return {
    ...omit(workOrder, ['jobs', 'job_site', 'client_id', 'wo_states', 'upcoming_job']),
    woStates: wo_states,
    jobSite: job_site ? cleanJobSite({ ...job_site }) : null,
    client: {
      id: client_id,
      name: client ? client.name : null,
      address: client ? client.address : '',
      email: client ? client.email : '',
      phone: client ? client.phone : '',
    },
    upcomingJob,
    assignee_ids: assignee_ids || [],
    assignees: sortAssignees(assignee_ids || [], wo_assignees),
    timeZoneAbbr: upcomingJob ? upcomingJob.timeZoneAbbr : null,
    jobs: get(workOrder, 'jobs', []).map((job) => cleanJob(job, tz, state, getters)),
  };
}

function cleanJobDateTime(job, tz) {
  // TODO: Make sure about this approach
  const stopsAt = moment.utc(job.stops_at);
  const startsAt = moment.utc(job.starts_at);

  if (tz) {
    stopsAt.tz(tz);
    startsAt.tz(tz);
  }

  // Account for jobs that expand across midnight
  if (stopsAt.isBefore(startsAt, 'minute')) {
    stopsAt.add(1, 'days');
  }

  return {
    stopsAt,
    startsAt,
  };
}

export function cleanJob(job, tz, state, getters) {
  const { id, details, status, assignee_ids, client_name, job_site, job_schedule_id } = job || {};
  const { attendance_shifts, actual_start_time, actual_stop_time, time_worked, notes: jobNotes, summary } = job || {};
  const { stopsAt, startsAt } = cleanJobDateTime(job, tz);
  const actualStopTime = actual_stop_time ? datetimeWithZone(actual_stop_time, tz) : null;
  const actualStartTime = actual_start_time ? datetimeWithZone(actual_start_time, tz) : null;
  const visits = (attendance_shifts || []).map((as) => ({
    date: as.date,
    stopTime: as.actual_stop_time ? datetimeWithZone(as.actual_stop_time, tz) : null,
    startTime: as.actual_start_time ? datetimeWithZone(as.actual_start_time, tz) : null,
    status: { symbol: as.status_sym, name: as.status_name },
    assignee: getters.organizationMembers.find((member) => member.id === as.user_id),
  }));
  const notes = jobNotes
    ? jobNotes.map((note) => ({
        ...note,
        attachments: note.attachments || [],
        author: getters.organizationMembers.find((member) => member.id === note.user_id),
      }))
    : null;
  let schedule = job_schedule_id
    ? get(state, 'job_schedules', []).find((schedule) => schedule.id === job_schedule_id)
    : null;
  const { visits_report_url: url } = state;
  const dateEnd = stopsAt.format('YYYY-MM-DD');
  const dateStart = startsAt.format('YYYY-MM-DD');
  const jobSiteFilterId = state.work_order?.job_site?.id;
  const queryParams = new URLSearchParams({
    date: dateStart,
    date_end: dateEnd,
    ...(jobSiteFilterId && { 'filters[job_sites][]': jobSiteFilterId }),
  }).toString();
  const visitsReportUrl = `${url}?${queryParams}`;

  if (schedule) {
    const scheduleDate = moment.utc(schedule.date);

    if (tz) {
      scheduleDate.tz(tz);
    }

    schedule = { ...schedule, date: scheduleDate };
  }

  return {
    id,
    notes,
    visits,
    status,
    details,
    summary,
    stopsAt,
    startsAt,
    schedule,
    visitsReportUrl,
    client: client_name,
    timeWorked: time_worked,
    actualStopTime,
    actualStartTime,
    // TODO: Make sure that this needs to be here...
    jobSite: cleanJobSite(job_site || {}),
    timeZoneAbbr: startsAt ? startsAt.zoneAbbr() : null,
    assignees: sortAssignees(assignee_ids || [], state.wo_assignees),
  };
}

export function cleanJobSite(jobSite) {
  const { latitude, longitude, enter_action, exit_action } = jobSite || {};

  return {
    ...omit(jobSite, ['latitude', 'longitude', 'enter_action', 'exit_action']),
    enter_action: enter_action || JOB_SITE_ACTIONS.doNothing,
    exit_action: exit_action || JOB_SITE_ACTIONS.doNothing,
    position: {
      lat: latitude ? Number(latitude) : null,
      lng: longitude ? Number(longitude) : null,
    },
  };
}
